import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react"

const cn = (...parts: Array<string | false | null | undefined>) => parts.filter(Boolean).join(" ")

export type ButtonVariant =
  | "primary"
  | "outline"
  | "outlineCompact"
  | "outlineSm"
  | "neutral"
  | "danger"
  | "dangerOutline"
  | "onDark"
  | "link"
  | "dashed"

export type ButtonSize = "sm" | "md" | "lg"

export type ButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> & {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
  /** Встроенный спиннер слева при `loading`. Выключите, если индикатор в `children` (например `InlineLoadingDots`). */
  showLoadingSpinner?: boolean
  leftIcon?: ReactNode
  rightIcon?: ReactNode
  fullWidth?: boolean
  children?: ReactNode
}

const baseFocus =
  "focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"

const sizeClasses: Record<ButtonSize, string> = {
  sm: "px-3 py-1.5 text-xs",
  md: "px-5 py-2.5 text-sm",
  lg: "px-6 py-3 text-sm",
}

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    "inline-flex items-center justify-center gap-2 bg-brand-500 text-white font-bold rounded-lg hover:bg-brand-700 focus-visible:ring-white focus-visible:ring-offset-brand-500",
  outline:
    "inline-flex items-center justify-center gap-2 rounded-lg border border-brand-500 text-brand-500 font-semibold hover:bg-brand-500 hover:text-white whitespace-nowrap focus-visible:ring-brand-500",
  outlineCompact:
    "inline-flex items-center justify-center gap-2 rounded-lg border border-brand-500 text-brand-500 font-semibold hover:bg-brand-500 hover:text-white whitespace-nowrap focus-visible:ring-brand-500 cursor-pointer",
  outlineSm:
    "inline-flex items-center justify-center rounded-lg border border-brand-500 text-brand-500 font-semibold hover:bg-brand-500 hover:text-white whitespace-nowrap focus-visible:ring-brand-500 cursor-pointer",
  neutral:
    "inline-flex items-center justify-center gap-2 rounded-lg border border-gray-200 text-gray-700 font-semibold hover:border-brand-500 hover:text-brand-500 whitespace-nowrap focus-visible:ring-brand-500 cursor-pointer",
  danger:
    "inline-flex items-center justify-center gap-2 rounded-lg bg-red-600 text-white font-bold hover:bg-red-700 focus-visible:ring-white focus-visible:ring-offset-red-600",
  dangerOutline:
    "inline-flex items-center justify-center gap-1.5 rounded-lg border border-red-200 bg-red-50 text-red-800 font-semibold hover:bg-red-100 hover:border-red-300 whitespace-nowrap focus-visible:ring-red-400 cursor-pointer disabled:opacity-40 disabled:pointer-events-none disabled:hover:border-red-200 disabled:hover:bg-red-50 disabled:hover:text-red-800",
  onDark:
    "relative inline-flex items-center justify-center bg-white text-brand-500 font-bold rounded-lg hover:bg-[#F5E6D3] whitespace-nowrap text-center focus-visible:ring-white focus-visible:ring-offset-brand-500 cta-glow",
  link:
    "inline-flex min-w-0 items-center gap-2 font-semibold text-gray-600 hover:text-brand-500 focus-visible:ring-brand-500 rounded-lg",
  dashed:
    "flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed border-gray-200 text-gray-500 hover:border-brand-500/50 hover:text-brand-500 focus-visible:ring-brand-500/40",
}

/**
 * Базовая кнопка действия. Для навигационных вариантов (navHeader, portalNav, footerText)
 * по-прежнему используется `ButtonLink` из `@/lib/ButtonLink`.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "primary",
      size = "md",
      loading = false,
      showLoadingSpinner = true,
      leftIcon,
      rightIcon,
      fullWidth = false,
      type = "button",
      className,
      disabled,
      children,
      ...rest
    },
    ref,
  ) => {
    const merged = cn(
      baseFocus,
      sizeClasses[size],
      variantClasses[variant],
      fullWidth && "w-full",
      className,
    )
    return (
      <button
        ref={ref}
        type={type}
        className={merged}
        disabled={disabled ?? loading}
        aria-busy={loading || undefined}
        {...rest}
      >
        {loading && showLoadingSpinner ? (
          <span
            aria-hidden="true"
            className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-r-transparent"
          />
        ) : (
          leftIcon
        )}
        {children}
        {!loading && rightIcon ? rightIcon : null}
      </button>
    )
  },
)

Button.displayName = "Button"
